import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import api, { handleApiError } from "../../services/api";

/**
 * Download / Upload controls for Tasks + Action Items.
 *
 * Download produces a two-sheet workbook (Tasks + Action Items, each row
 * carrying the Task ID so the sheets relink on re-import): XLSX natively, or a
 * zip of two CSVs. It exports the rows currently visible on the page (whose ids
 * `getVisibleTaskIds` returns) unless "Whole workspace" is ticked.
 *
 * Upload upserts by ID and reports any skipped rows; `onImported` refreshes the
 * page afterwards.
 *
 * @param {Object} props
 * @param {number|string} props.workspaceId
 * @param {() => number[]} props.getVisibleTaskIds
 * @param {() => void} [props.onImported]
 */
const TaskImportExport = ({ workspaceId, getVisibleTaskIds, onImported }) => {
  const fileInputRef = useRef(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [wholeWorkspace, setWholeWorkspace] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  // Upload modal state. `phase`: uploading -> processing -> done | error.
  const [importOpen, setImportOpen] = useState(false);
  const [phase, setPhase] = useState("uploading");
  const [uploadPct, setUploadPct] = useState(0);
  const [fileName, setFileName] = useState("");
  const [report, setReport] = useState(null);
  const [importError, setImportError] = useState("");

  const uploading = importOpen && (phase === "uploading" || phase === "processing");

  const filenameFromHeaders = (headers, fallback) => {
    const disposition = headers?.["content-disposition"] || "";
    const match = /filename="?([^"]+)"?/.exec(disposition);
    return match ? match[1] : fallback;
  };

  const handleDownload = async (format) => {
    setMenuAnchor(null);
    setError("");
    if (!workspaceId) {
      setError("No workspace selected.");
      return;
    }
    setDownloading(true);
    try {
      const body = { workspace_id: workspaceId, format };
      if (wholeWorkspace) {
        body.scope = "all";
      } else {
        body.scope = "visible";
        body.task_ids = getVisibleTaskIds ? getVisibleTaskIds() : [];
      }
      const response = await api.post("tasks/export/", body, {
        responseType: "blob",
      });
      const fallback =
        format === "csv" ? "tasks_export.zip" : "tasks_export.xlsx";
      const filename = filenameFromHeaders(response.headers, fallback);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    // Reset so re-selecting the same file still fires onChange.
    event.target.value = "";
    if (!file) return;
    if (!workspaceId) {
      setError("No workspace selected.");
      return;
    }

    // Open the modal immediately and drive it through the upload phases.
    setError("");
    setReport(null);
    setImportError("");
    setFileName(file.name);
    setUploadPct(0);
    setPhase("uploading");
    setImportOpen(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("workspace_id", workspaceId);
      const response = await api.post("tasks/import/", formData, {
        onUploadProgress: (e) => {
          if (e.total) {
            const pct = Math.round((e.loaded * 100) / e.total);
            setUploadPct(pct);
            // Bytes are on the wire; the server is now parsing/upserting.
            if (pct >= 100) setPhase("processing");
          }
        },
      });
      setReport(response.data);
      setPhase("done");
      onImported?.();
    } catch (err) {
      setImportError(handleApiError(err));
      setPhase("error");
    }
  };

  const closeImportModal = () => {
    if (uploading) return; // don't allow closing mid-flight
    setImportOpen(false);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <FormControlLabel
        sx={{ mr: 0 }}
        control={
          <Checkbox
            size="small"
            checked={wholeWorkspace}
            onChange={(e) => setWholeWorkspace(e.target.checked)}
          />
        }
        label={<Typography variant="caption">Whole workspace</Typography>}
      />

      <Button
        variant="contained"
        size="small"
        startIcon={
          downloading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <DownloadIcon />
          )
        }
        disabled={downloading}
        onClick={(e) => setMenuAnchor(e.currentTarget)}
      >
        Download
      </Button>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleDownload("xlsx")}>Excel (.xlsx)</MenuItem>
        <MenuItem onClick={() => handleDownload("csv")}>
          CSV (.zip &mdash; 2 files)
        </MenuItem>
      </Menu>

      <Button
        variant="outlined"
        size="small"
        startIcon={
          uploading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <UploadIcon />
          )
        }
        disabled={uploading}
        onClick={() => fileInputRef.current?.click()}
      >
        Upload
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.zip,.csv"
        hidden
        onChange={handleFileSelected}
      />

      {error && (
        <Alert severity="error" sx={{ py: 0 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <ImportProgressDialog
        open={importOpen}
        phase={phase}
        uploadPct={uploadPct}
        fileName={fileName}
        report={report}
        error={importError}
        onClose={closeImportModal}
      />
    </Box>
  );
};

/**
 * Modal that walks an upload through its phases and then shows the result.
 * While uploading/processing it is non-dismissable (no backdrop/escape close,
 * no close button); once done or errored the user can close it.
 */
const ImportProgressDialog = ({
  open,
  phase,
  uploadPct,
  fileName,
  report,
  error,
  onClose,
}) => {
  const busy = phase === "uploading" || phase === "processing";
  const skipped = report?.skipped || [];

  const title =
    phase === "uploading"
      ? "Uploading file…"
      : phase === "processing"
        ? "Processing import…"
        : phase === "error"
          ? "Import failed"
          : "Import complete";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={busy}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {fileName && (
          <Typography variant="caption" color="text.secondary">
            {fileName}
          </Typography>
        )}

        {phase === "uploading" && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadPct} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Uploading… {uploadPct}%
            </Typography>
          </Box>
        )}

        {phase === "processing" && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Reading sheets and applying rows on the server…
            </Typography>
          </Box>
        )}

        {phase === "error" && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <ErrorOutlineIcon color="error" />
            <Typography variant="body2">
              {error || "Something went wrong during import."}
            </Typography>
          </Stack>
        )}

        {phase === "done" && report && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <CheckCircleOutlineIcon color="success" />
              <Typography variant="subtitle2">Finished</Typography>
            </Stack>
            <Typography variant="body2" gutterBottom>
              Tasks: {report.tasks_created} created, {report.tasks_updated}{" "}
              updated
            </Typography>
            <Typography variant="body2" gutterBottom>
              Action items: {report.action_items_created} created,{" "}
              {report.action_items_updated} updated
            </Typography>

            {skipped.length > 0 ? (
              <>
                <Alert severity="warning" sx={{ mt: 2, mb: 1 }}>
                  {skipped.length} row{skipped.length === 1 ? "" : "s"} skipped.
                </Alert>
                <Box sx={{ maxHeight: 240, overflow: "auto" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Sheet</TableCell>
                        <TableCell>Row</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {skipped.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell>{s.sheet}</TableCell>
                          <TableCell>{s.row}</TableCell>
                          <TableCell>{s.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </>
            ) : (
              <Alert severity="success" sx={{ mt: 2 }}>
                All rows imported successfully.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskImportExport;
